// Componentes de UI reutilizables — sistema de diseño "Audiophile Precision".
// Las firmas (props) se mantienen estables para no romper las vistas; solo
// cambia el markup/estilo al nuevo sistema (Tailwind + tokens de Stitch).

import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollFocusOnMount } from './useScrollFocusOnMount.js';
import { useAuth } from '../auth/AuthContext.js';

/** Icono Material Symbols. */
export function Icon({ name, className = '' }: { name: string; className?: string }) {
  return <span className={`material-symbols-outlined ${className}`}>{name}</span>;
}

/** Indicador de carga (pulso de aguja). */
export function Spinner({ label }: { label?: string }) {
  const { t } = useTranslation();
  const text = label ?? t('common.loading');
  return (
    <div
      className="flex items-center justify-center gap-space-sm py-space-xl text-on-surface-variant font-code-catalog text-body-sm"
      role="status"
      aria-live="polite"
    >
      <span className="w-2.5 h-2.5 rounded-full bg-primary-container animate-pulse shadow-[0_0_8px_rgba(56,189,248,0.5)]" />
      {text}
    </div>
  );
}

/**
 * Spinner INLINE para botones/acciones que disparan procesos lentos (sync,
 * aplicar cambios, guardar, identificar…). Un aro girando que hereda el color
 * del texto (`currentColor`), para indicar "procesando" mientras llega la
 * respuesta. Tamaño ajustable vía `className`.
 */
export function ButtonSpinner({ className = 'text-[16px]' }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      role="status"
      aria-label={t('ui.processing')}
      className={`inline-block aspect-square w-[1em] shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent align-[-0.125em] ${className}`}
    />
  );
}

/**
 * Overlay de PROCESAMIENTO a pantalla completa para operaciones lentas que
 * bloquean la interacción (p. ej. iniciar una ingesta, exportar). Muestra un
 * spinner centrado con un mensaje sobre un velo translúcido. Renderiza `null`
 * cuando `show` es falso.
 */
export function ProcessingOverlay({ show, label }: { show: boolean; label?: string }) {
  const { t } = useTranslation();
  if (!show) return null;
  const text = label ?? t('common.processing');
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-base/70 backdrop-blur-sm"
      role="status"
      aria-live="assertive"
    >
      <div className="flex flex-col items-center gap-space-sm rounded-2xl bg-surface-deck/95 border border-border-subtle px-space-xl py-space-lg shadow-2xl">
        <ButtonSpinner className="text-[32px] text-primary" />
        <span className="font-body-md text-body-md text-on-surface">{text}</span>
      </div>
    </div>
  );
}

/** Aviso de error con opción de reintento (Req. 17.4). */
export function ErrorNotice({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  // Al aparecer el error (o cambiar su texto), lleva el foco y el scroll al
  // mensaje para que el usuario lo vea aunque esté desplazado hacia abajo.
  const ref = useScrollFocusOnMount<HTMLDivElement>(true, [message]);
  return (
    <div
      ref={ref}
      tabIndex={-1}
      className="flex items-center justify-between gap-space-sm p-space-md rounded-lg bg-error-container/20 border border-error/40 text-error outline-none scroll-mt-24"
      role="alert"
    >
      <span className="flex items-center gap-space-xs font-body-sm text-body-sm">
        <Icon name="error" className="text-[18px]" />
        {message}
      </span>
      {onRetry && (
        <button
          type="button"
          className="btn-secondary btn-sm !text-error !border-error/40"
          onClick={onRetry}
        >
          {t('ui.retry')}
        </button>
      )}
    </div>
  );
}

/**
 * Mensaje de error o advertencia INLINE para formularios/paneles. Reemplaza el
 * patrón repetido `{error && <div className="...text-error...">{error}</div>}`
 * de las vistas y, además, al aparecer lleva el foco y el scroll al mensaje
 * (Req. accesibilidad: el usuario debe enterarse sin desplazarse). Para
 * lectores de pantalla declara `role` (`alert` en error, `status` en
 * advertencia) con la cortesía `aria-live` correspondiente.
 *
 * No renderiza nada si `message` es vacío/nulo, así que puede usarse como
 * `<AlertMessage message={error} />` sin el guard `{error && ...}`.
 */
export function AlertMessage({
  message,
  tone = 'error',
  className = '',
}: {
  message?: string | null;
  tone?: 'error' | 'warning';
  className?: string;
}) {
  const visible = typeof message === 'string' && message.trim() !== '';
  const ref = useScrollFocusOnMount<HTMLDivElement>(visible, [message]);
  if (!visible) return null;

  const styles =
    tone === 'warning'
      ? 'bg-goldmine-vg/15 border-goldmine-vg/40 text-goldmine-vg'
      : 'bg-error-container/20 border-error/40 text-error';
  const icon = tone === 'warning' ? 'warning' : 'error';

  return (
    <div
      ref={ref}
      tabIndex={-1}
      role={tone === 'warning' ? 'status' : 'alert'}
      aria-live={tone === 'warning' ? 'polite' : 'assertive'}
      className={`flex items-start gap-space-xs p-space-sm rounded-lg border font-body-sm text-body-sm outline-none scroll-mt-24 ${styles} ${className}`}
    >
      <Icon name={icon} className="text-[18px] shrink-0 mt-px" />
      <span>{message}</span>
    </div>
  );
}

/**
 * Estado vacío. Admite opcionalmente un título, un icono y una ACCIÓN (CTA) para
 * los estados vacíos "con coaching" (p. ej. catálogo recién creado → "Agrega tu
 * primer disco"), que guían al usuario hacia la acción de mayor valor en vez de
 * dejar solo un texto plano.
 */
export function EmptyState({
  message,
  title,
  icon = 'inbox',
  action,
}: {
  message: string;
  title?: string;
  icon?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-space-sm py-space-xl px-space-md text-center text-on-surface-variant">
      <span className="w-16 h-16 rounded-full bg-surface-container-high border border-border-subtle flex items-center justify-center">
        <Icon name={icon} className="text-[32px] opacity-70 text-primary" />
      </span>
      {title && (
        <span className="font-headline-sm text-headline-sm text-on-surface font-bold">{title}</span>
      )}
      <span className="font-body-sm text-body-sm max-w-xs">{message}</span>
      {action && <div className="mt-space-sm">{action}</div>}
    </div>
  );
}

/**
 * Bloque "skeleton" (placeholder animado) para la carga de contenido. Se percibe
 * más rápido que un spinner y evita el salto de layout cuando llegan los datos.
 * Respeta `prefers-reduced-motion` (sin pulso) vía la clase `motion-safe`.
 */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={`rounded bg-surface-container-high motion-safe:animate-pulse ${className}`}
    />
  );
}

/** Skeleton con la geometría aproximada de una `RecordCard` del catálogo. */
export function SkeletonCard() {
  return (
    <div className="surface-card flex gap-space-sm">
      <Skeleton className="w-20 h-20 shrink-0 rounded-lg" />
      <div className="flex-1 min-w-0 flex flex-col gap-space-xs py-1">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
        <div className="flex gap-space-xs mt-auto">
          <Skeleton className="h-5 w-12 rounded-full" />
          <Skeleton className="h-5 w-12 rounded-full" />
        </div>
      </div>
    </div>
  );
}

/** Lista de N skeletons de tarjeta (para listas en su primer loading). */
export function SkeletonList({ count = 5 }: { count?: number }) {
  return (
    <div
      className="flex flex-col gap-space-sm"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

/**
 * Envoltura estándar de estados async: loading / error / vacío / contenido.
 * Admite opcionalmente un `fallback` de carga (p. ej. skeletons de lista) que
 * se muestra en el primer loading en vez del spinner por defecto.
 */
export function AsyncBoundary<T>({
  state,
  children,
  empty,
  fallback,
}: {
  state: { data: T | null; loading: boolean; error: string | null; reload: () => void };
  children: (data: T) => ReactNode;
  empty?: (data: T) => boolean;
  /** Contenido alternativo durante el primer loading (por defecto, Spinner). */
  fallback?: ReactNode;
}) {
  const { t } = useTranslation();
  if (state.loading && state.data === null) return <>{fallback ?? <Spinner />}</>;
  if (state.error) return <ErrorNotice message={state.error} onRetry={state.reload} />;
  if (state.data === null) return <EmptyState message={t('ui.noData')} />;
  if (empty && empty(state.data)) return <EmptyState message={t('ui.noResults')} />;
  return <>{children(state.data)}</>;
}

/** Etiqueta/badge coloreada (tonos genéricos). */
export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warn' | 'info' | 'danger';
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-surface-container-high text-on-surface-variant border-border-subtle',
    success: 'bg-goldmine-mint/15 text-goldmine-mint border-goldmine-mint/40',
    warn: 'bg-goldmine-vg/15 text-goldmine-vg border-goldmine-vg/40',
    info: 'bg-primary-container/15 text-primary border-primary-container/40',
    danger: 'bg-priority-urgent/15 text-priority-urgent border-priority-urgent/40',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded font-badge-micro text-badge-micro font-bold uppercase tracking-wider border ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/** Grading Goldmine (M/NM/VG+/VG/G/P) con color estándar. */
export function GoldmineBadge({ grade }: { grade: string }) {
  const g = grade.trim().toUpperCase();
  const map: Record<string, string> = {
    M: 'bg-goldmine-mint/15 text-goldmine-mint border-goldmine-mint',
    NM: 'bg-goldmine-near-mint/15 text-goldmine-near-mint border-goldmine-near-mint',
    'VG+': 'bg-goldmine-vg-plus/15 text-goldmine-vg-plus border-goldmine-vg-plus',
    VG: 'bg-goldmine-vg/15 text-goldmine-vg border-goldmine-vg',
    G: 'bg-goldmine-g/15 text-goldmine-g border-goldmine-g',
    P: 'bg-goldmine-poor/15 text-goldmine-poor border-goldmine-poor',
  };
  const cls = map[g] ?? 'bg-surface-container-high text-on-surface-variant border-border-subtle';
  return <span className={`badge-goldmine ${cls}`}>{grade}</span>;
}

/** Panel/tarjeta (Deck). */
export function Card({
  children,
  title,
  icon,
  action,
  collapsible = false,
  defaultOpen = true,
  className,
}: {
  children: ReactNode;
  title?: string;
  icon?: string;
  action?: ReactNode;
  /** Si es true, el título actúa como toggle para mostrar/ocultar el contenido. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Clases extra para el contenedor (p. ej. para resaltar la tarjeta). */
  className?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const titleNode = title && (
    <h2 className="flex items-center gap-space-xs font-headline-sm text-headline-sm text-on-surface uppercase tracking-wider">
      {icon && <Icon name={icon} className="text-primary text-[20px]" />}
      {title}
    </h2>
  );

  // Modo colapsable: la cabecera es un botón que alterna el contenido.
  if (collapsible && title) {
    return (
      <section className={`surface-card mb-space-md ${className ?? ''}`}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-space-sm"
          aria-expanded={open}
        >
          {titleNode}
          <span className="flex items-center gap-space-sm">
            {action}
            <Icon
              name="expand_more"
              className={`text-on-surface-variant text-[22px] transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </span>
        </button>
        {open && <div className="mt-space-md">{children}</div>}
      </section>
    );
  }

  return (
    <section className={`surface-card mb-space-md ${className ?? ''}`}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-space-sm mb-space-md">
          {titleNode}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** Grupo de campos plegable (para la búsqueda avanzada, Req. 3.2). */
export function Collapsible({
  title,
  open,
  onToggle,
  children,
  icon,
  meta,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  icon?: string;
  meta?: ReactNode;
}) {
  return (
    <div className="accordion mb-space-sm">
      <button
        type="button"
        className="accordion-header"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="flex items-center gap-space-xs">
          {icon && <Icon name={icon} className="text-primary text-[20px]" />}
          {title}
        </span>
        <span className="flex items-center gap-space-sm">
          {meta}
          <Icon
            name="expand_more"
            className={`text-[22px] text-on-surface-variant transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      {open && <div className="p-space-md border-t border-border-subtle">{children}</div>}
    </div>
  );
}

/** Encabezado de página (hero module). */
export function PageHeader({
  title,
  subtitle,
  meta,
  actions,
  back,
}: {
  /** Retirado del render por diseño; se acepta por compatibilidad con las páginas. */
  eyebrow?: string;
  title: string;
  subtitle?: string;
  meta?: ReactNode;
  actions?: ReactNode;
  /**
   * Muestra un botón "Volver" encima del título. `true` regresa al Menú (el hub
   * desde donde se llega a las secciones profundas); una ruta `string` navega a
   * esa ruta. Útil para que ninguna sección quede sin salida sin usar la pestaña.
   */
  back?: boolean | string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const backTo = typeof back === 'string' ? back : '/menu';

  return (
    <div className="px-margin-mobile py-space-md bg-surface-deck border-b border-border-subtle">
      {back && (
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="flex items-center gap-space-xs text-on-surface-variant hover:text-primary font-body-sm text-body-sm mb-space-xs active:scale-95 transition-transform"
          aria-label={t('ui.backToMenu')}
        >
          <Icon name="arrow_back" className="text-[18px]" /> {t('common.back')}
        </button>
      )}
      <div className="flex flex-col gap-space-xs">
        {/* El "eyebrow" (primera línea) se retiró por diseño: solo título + descripción.
            A la derecha del título: `meta` (acciones de la vista, p. ej. exportar)
            y el botón "Salir" (icono), SIEMPRE presente. El título ocupa el
            espacio restante (`flex-1 min-w-0`) para no desplazar ni tapar los
            botones de la derecha. */}
        <div className="flex items-start justify-between gap-space-sm">
          <h1 className="flex-1 min-w-0 font-headline-lg-mobile text-headline-lg-mobile text-vacuum-glow tracking-tight font-bold">
            {title}
          </h1>
          <div className="shrink-0 flex items-center gap-space-xs">
            {meta}
            <button
              type="button"
              onClick={() => {
                // Cierra sesión y lleva al home público (no a /login). La propia
                // redirección por falta de sesión también apunta a "/".
                void signOut().then(() => navigate('/'));
              }}
              className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-surface-rack text-priority-urgent active:scale-95 transition-transform"
              aria-label={t('ui.signOut')}
              title={t('ui.signOut')}
            >
              <Icon name="logout" className="text-[20px]" />
            </button>
          </div>
        </div>
        {subtitle && <p className="font-body-sm text-body-sm text-on-surface-variant">{subtitle}</p>}
      </div>
      {actions && <div className="mt-space-md">{actions}</div>}
    </div>
  );
}
